import type { SequenceRead } from './sequenceHead.ts'
import { bestPrefixEditDistance, reverseComplement, toMasks } from './iupac.ts'
import type { KnownPrimerPair } from './primerRegistry.ts'
import { stripAdapter } from './primerRegistry.ts'
import type { PrimerPair } from './primerTrimDetection.ts'

export type TaggedPrimerConfidence = 'high' | 'low' | 'none'

export type PrefixShare = { prefix: string; share: number }

/** Gene primers to look for, optionally with the registered spellings an exact primer should be written in. */
export type GenePrimers = PrimerPair & { forwardVariants?: readonly string[]; reverseVariants?: readonly string[] }

export type TaggedPrimerSide = {
  /** The gene primer the reads were searched for (a registry union may be broader than any real primer). */
  genePrimer: string
  /** The registered spelling of the gene primer that fits the reads, least degenerate among near-ties. */
  variant: string | null
  /** Every registered spelling that fits the reads as well as `variant` does, `variant` included. */
  tiedVariants: string[]
  /** The exact primer the sample was amplified with — prefix + variant — or `null` with no verdict. */
  primer: string | null
  /** The bases in front of the gene primer on most reads — tag, spacer, or both. `''` means untagged. */
  prefix: string | null
  /**
   * The prefix is a tag behind a heterogeneity spacer: the reads agree on the bases next to the primer but
   * not on the random bases in front, so `prefix` is only the agreed part and `primer` leaves the spacer out.
   */
  spacer: boolean
  /** Share of the reads carrying the gene primer whose prefix is the dominant one. */
  support: number
  /** Reads on which the gene primer was found within the prefix window. */
  located: number
  /** The most common prefixes, dominant first. */
  top: PrefixShare[]
  confidence: TaggedPrimerConfidence
  reason: string
}

export type TaggedPrimerEstimate = { forward: TaggedPrimerSide; reverse: TaggedPrimerSide }

export type TaggedPrimerOptions = {
  /** Longest prefix looked for: tags seen in prod reach 12 bases, spacers add up to 8. */
  maxPrefix: number
  maxErrorRate: number
  minLocated: number
  highSupportAtLeast: number
  lowSupportAtLeast: number
  /** A spelling scoring within this many mean edits per read of the best one counts as a tie, at the gene primer's length. */
  variantTie: number
}

export const DEFAULT_TAGGED_PRIMER_OPTIONS: TaggedPrimerOptions = {
  maxPrefix: 24,
  maxErrorRate: 0.15,
  minLocated: 20,
  highSupportAtLeast: 0.7,
  lowSupportAtLeast: 0.4,
  variantTie: 0.1
}

type Gene = { sequence: string; masks: number[]; maxErrors: number }

const MIN_SPACED_TAG = 4

const prepare = (primer: string, options: TaggedPrimerOptions): Gene | null => {
  const sequence = stripAdapter(primer)
  if (sequence.length < 12) {
    return null
  }
  return { sequence, masks: toMasks(sequence), maxErrors: Math.floor(sequence.length * options.maxErrorRate) }
}

/** Where the whole gene primer best aligns within the first bases, with its edit distance; the earliest start wins ties. */
const locate = (read: string, gene: Gene, maxPrefix: number): { start: number; distance: number } | null => {
  let found: { start: number; distance: number } | null = null
  for (let s = 0; s <= maxPrefix && s + gene.masks.length <= read.length; s++) {
    const distance = bestPrefixEditDistance(gene.masks, read.slice(s), 0, found ? Math.min(found.distance, gene.maxErrors) : gene.maxErrors)
    if (distance <= gene.maxErrors && (!found || distance < found.distance)) {
      found = { start: s, distance }
    }
    if (found?.distance === 0) {
      break
    }
  }
  return found
}

type Located = { end: string; start: number }

/** The spacer-tolerant fallback: the longest run of bases right before the primer that most reads share. */
const sharedSuffix = (prefixes: readonly string[], share: number): string => {
  let best = ''
  for (let k = 1; ; k++) {
    const counts = new Map<string, number>()
    for (const prefix of prefixes) {
      if (prefix.length >= k) {
        const suffix = prefix.slice(-k)
        counts.set(suffix, (counts.get(suffix) ?? 0) + 1)
      }
    }
    const top = [...counts].sort((a, b) => b[1] - a[1])[0]
    if (!top || top[1] / prefixes.length < share) {
      return best
    }
    best = top[0]
  }
}

/** Per base: how many of A/C/G/T beyond one it allows; inosine pairs with all four, so it counts as `N`. */
const degeneracy = (variant: string): { codes: number; folds: number } =>
  toMasks(variant).reduce(
    (sum, mask) => {
      const extra = [1, 2, 4, 8].filter((bit) => mask & bit).length - 1
      return extra > 0 ? { codes: sum.codes + 1, folds: sum.folds + extra } : sum
    },
    { codes: 0, folds: 0 }
  )

/** The most specific spelling: fewest ambiguity codes, then the least degenerate ones; registry order breaks ties. */
const leastDegenerate = (variants: readonly string[]): string =>
  variants
    .map((variant) => ({ variant, ...degeneracy(variant) }))
    .reduce((best, next) => (next.codes < best.codes || (next.codes === best.codes && next.folds < best.folds) ? next : best)).variant

/**
 * Picks the registered spelling that fits the reads; among near-ties the longest, then the least degenerate.
 * Spellings are 3′-aligned inside the union, never longer.
 */
const pickVariant = (
  gene: Gene,
  variants: readonly string[],
  located: readonly Located[],
  options: TaggedPrimerOptions
): { variant: string; tied: string[] } => {
  const scored = variants.flatMap((variant) => {
    const delta = gene.sequence.length - variant.length
    if (delta < 0 || variant.length < 12) {
      return []
    }
    const masks = toMasks(variant)
    const cutoff = Math.floor(variant.length * options.maxErrorRate)
    const total = located.reduce((sum, l) => sum + bestPrefixEditDistance(masks, l.end.slice(l.start + delta), 0, cutoff), 0)
    /* per base, scaled to the gene primer: a shorter spelling has fewer bases to collect read errors on */
    return [{ variant, score: (total / Math.max(1, located.length)) * (gene.sequence.length / variant.length) }]
  })
  if (scored.length === 0) {
    return { variant: gene.sequence, tied: [] }
  }
  const best = Math.min(...scored.map((v) => v.score))
  const tied = scored.filter((v) => v.score <= best + options.variantTie).map((v) => v.variant)
  const longest = Math.max(...tied.map((v) => v.length))
  return { variant: leastDegenerate(tied.filter((v) => v.length === longest)), tied }
}

const mostCommon = (values: readonly string[]): string => {
  const counts = new Map<string, number>()
  for (const value of values) {
    counts.set(value, (counts.get(value) ?? 0) + 1)
  }
  return [...counts].sort((a, b) => b[1] - a[1])[0]?.[0] ?? ''
}

const side = (
  gene: Gene | null,
  primer: string,
  variants: readonly string[],
  located: readonly Located[],
  options: TaggedPrimerOptions
): TaggedPrimerSide => {
  const genePrimer = gene?.sequence ?? stripAdapter(primer)
  const prefixes = located.map((l) => l.end.slice(0, l.start))
  const none = (reason: string, top: PrefixShare[] = [], support = 0): TaggedPrimerSide => ({
    genePrimer,
    variant: null,
    tiedVariants: [],
    primer: null,
    prefix: null,
    spacer: false,
    support,
    located: prefixes.length,
    top,
    confidence: 'none',
    reason
  })
  if (!gene) {
    return none('no usable gene primer')
  }
  if (prefixes.length < options.minLocated) {
    return none(`gene primer found on only ${prefixes.length} reads`)
  }
  const counts = new Map<string, number>()
  for (const prefix of prefixes) {
    counts.set(prefix, (counts.get(prefix) ?? 0) + 1)
  }
  const top = [...counts]
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5)
    .map(([prefix, count]) => ({ prefix, share: count / prefixes.length }))
  const [dominant] = top
  /* only below low support: a merely weak dominant prefix is usually read errors in the tag's first bases, not a spacer */
  if (dominant.share < options.lowSupportAtLeast) {
    const tag = sharedSuffix(prefixes, options.highSupportAtLeast)
    if (tag.length >= MIN_SPACED_TAG) {
      const carrying = located.filter((l) => l.end.slice(0, l.start).endsWith(tag))
      const { variant, tied } = pickVariant(gene, variants.length > 0 ? variants : [gene.sequence], carrying, options)
      const extra = mostCommon(carrying.map((l) => l.end.slice(l.start, l.start + gene.sequence.length - variant.length)))
      return {
        genePrimer,
        variant,
        tiedVariants: tied,
        primer: tag + extra + variant,
        prefix: tag,
        spacer: true,
        support: carrying.length / prefixes.length,
        located: prefixes.length,
        top,
        confidence: 'low',
        reason: `a ${tag.length}-base tag behind a variable spacer`
      }
    }
  }
  if (dominant.share < options.lowSupportAtLeast) {
    return none(`${counts.size} different prefixes, the most common on ${Math.round(dominant.share * 100)}% of reads`, top, dominant.share)
  }
  const carrying = located.filter((l) => l.start === dominant.prefix.length && l.end.startsWith(dominant.prefix))
  const { variant, tied } = pickVariant(gene, variants.length > 0 ? variants : [gene.sequence], carrying, options)
  const extra = mostCommon(carrying.map((l) => l.end.slice(l.start, l.start + gene.sequence.length - variant.length)))
  return {
    genePrimer,
    variant,
    tiedVariants: tied,
    primer: dominant.prefix + extra + variant,
    prefix: dominant.prefix,
    spacer: false,
    support: dominant.share,
    located: prefixes.length,
    top,
    confidence: dominant.share >= options.highSupportAtLeast ? 'high' : 'low',
    reason:
      dominant.prefix === ''
        ? 'the gene primer opens the reads with nothing in front'
        : `${dominant.prefix.length} bases in front of the gene primer`
  }
}

/**
 * The exact per-sample primers (tag + gene primer): for each gene primer, the dominant sequence in front
 * of it, in the order of `genePrimers`. Paired mates are read at their 5′ start only (a mate running through
 * a short amplicon shows adapter at its 3′ end); merged and single-end reads from both ends. `genePrimers`
 * must be the bare registered pair, or the tag would be matched away.
 */
export const estimateTaggedPrimers = (
  files: readonly (readonly SequenceRead[])[],
  genePrimers: GenePrimers,
  options: TaggedPrimerOptions = DEFAULT_TAGGED_PRIMER_OPTIONS
): TaggedPrimerEstimate => {
  const forward = prepare(genePrimers.forward, options)
  const reverse = prepare(genePrimers.reverse, options)
  const genes = [forward, reverse].flatMap((g, i) => (g ? [{ gene: g, index: i }] : []))
  const located: [Located[], Located[]] = [[], []]
  const paired = files.length > 1
  const window = options.maxPrefix + Math.max(0, ...genes.map((g) => g.gene.masks.length + Math.ceil(g.gene.masks.length / 4)))
  for (const reads of files) {
    for (const read of reads) {
      const ends = paired ? [read.sequence] : [read.sequence, reverseComplement(read.sequence.slice(-window))]
      for (const end of ends) {
        let best: { index: number; start: number; distance: number } | null = null
        for (const { gene, index } of genes) {
          const hit = locate(end, gene, options.maxPrefix)
          if (hit && (!best || hit.distance < best.distance)) {
            best = { index, ...hit }
          }
        }
        if (best) {
          located[best.index].push({ end, start: best.start })
        }
      }
    }
  }
  return {
    forward: side(forward, genePrimers.forward, genePrimers.forwardVariants ?? [], located[0], options),
    reverse: side(reverse, genePrimers.reverse, genePrimers.reverseVariants ?? [], located[1], options)
  }
}

/** A known pair as {@link GenePrimers}, `first` as forward unless `swapped`. */
export const genePrimersOf = (pair: KnownPrimerPair, swapped = false): GenePrimers =>
  swapped
    ? { forward: pair.second, reverse: pair.first, forwardVariants: pair.secondVariants, reverseVariants: pair.firstVariants }
    : { forward: pair.first, reverse: pair.second, forwardVariants: pair.firstVariants, reverseVariants: pair.secondVariants }
